import { useState } from 'react';

export default function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white font-playfair">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            {/* Left Navigation - Desktop */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="/for-attorneys" className="text-gray-900 hover:text-teal-600 font-medium tracking-wide transition-colors">For Attorneys</a>
              <a href="#services" className="text-gray-900 hover:text-teal-600 font-medium tracking-wide transition-colors">Services</a>
            </div>

            {/* Centered Logo */}
            <div className="flex items-center justify-center flex-grow md:flex-grow-0">
              <h1>
                <img 
                  src="https://static.readdy.ai/image/a19705f045df358eda503236ce656b36/846ee1e93f81f94b53dc3e9fa39ac791.png" 
                  alt="VTC Legal - Fractional General Counsel for AI and Tech Companies" 
                  className="h-20 w-auto"
                />
              </h1>
            </div>
            
            {/* Right Navigation - Desktop */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="/about" className="text-gray-900 hover:text-teal-600 font-medium tracking-wide transition-colors">About</a>
              <a href="#how-it-works" className="text-gray-900 hover:text-teal-600 font-medium tracking-wide transition-colors">How It Works</a>
              <a href="#contact" className="bg-black text-white px-8 py-3 rounded-md hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer font-medium">Get Started</a>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden absolute right-4">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-900 hover:text-teal-600 cursor-pointer"
              >
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col space-y-4">
                <a href="/for-attorneys" className="text-gray-900 hover:text-teal-600 font-medium">For Attorneys</a>
                <a href="#services" className="text-gray-900 hover:text-teal-600 font-medium">Services</a>
                <a href="/about" className="text-gray-900 hover:text-teal-600 font-medium">About</a>
                <a href="#how-it-works" className="text-gray-900 hover:text-teal-600 font-medium">How It Works</a>
                <a href="#contact" className="bg-black text-white px-8 py-3 rounded-md hover:bg-gray-800 transition-colors text-center cursor-pointer font-medium">Get Started</a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-white py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-6xl md:text-7xl font-bold text-gray-900 mb-10 leading-tight font-playfair">
              Fractional General Counsel for
              <span className="text-teal-600"> SaaS & AI</span> Companies
            </h2>
            <p className="text-xl text-gray-700 mb-12 max-w-4xl mx-auto leading-relaxed font-inter">
              Most SaaS and AI companies overpay for legal expertise or risk operating without proper counsel. Get Fortune 500-level legal strategy at a predictable monthly cost.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a href="#contact" className="bg-black text-white px-12 py-4 rounded-lg text-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer font-inter">
                Schedule Consultation
              </a>
              <a href="/about" className="border-2 border-black text-black px-12 py-4 rounded-lg text-lg font-semibold hover:bg-black hover:text-white transition-colors whitespace-nowrap cursor-pointer font-inter">
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Simplified */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-gray-900 mb-6 font-playfair">Legal Support for Growing Tech Companies</h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto font-inter">
              Strategic legal guidance tailored for technology companies at every stage
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <a href="/ai-governance" className="bg-gray-50 p-10 rounded-lg shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300 block cursor-pointer">
              <div className="w-16 h-16 bg-gray-900 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-cpu-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">AI Governance & Compliance</h3>
              <p className="text-gray-700 leading-relaxed font-inter">
                Navigate evolving AI regulations with compliant governance frameworks and risk mitigation strategies.
              </p>
            </a>

            <a href="/commercial-strategy" className="bg-gray-50 p-10 rounded-lg shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300 block cursor-pointer">
              <div className="w-16 h-16 bg-gray-900 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-handshake-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Commercial Strategy</h3>
              <p className="text-gray-700 leading-relaxed font-inter">
                Draft and negotiate commercial contracts that protect your interests while enabling growth.
              </p>
            </a>

            <a href="/corporate-law" className="bg-gray-50 p-10 rounded-lg shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300 block cursor-pointer">
              <div className="w-16 h-16 bg-gray-900 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-building-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Corporate Law</h3>
              <p className="text-gray-700 leading-relaxed font-inter">
                Build solid legal foundations with expert guidance on corporate governance and compliance.
              </p>
            </a>

            <a href="/strategic-planning" className="bg-gray-50 p-10 rounded-lg shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300 block cursor-pointer">
              <div className="w-16 h-16 bg-gray-900 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-lightbulb-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Strategic Planning</h3>
              <p className="text-gray-700 leading-relaxed font-inter">
                Align legal strategy with business objectives, considering both risk and opportunity.
              </p>
            </a>
          </div>
        </div>
      </section>

      {/* Key Benefits - Simplified */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-gray-900 mb-6 font-playfair">Why Choose Fractional Legal</h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto font-inter">
              Strategic advantages that deliver measurable business value
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-8">
                <i className="ri-funds-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">70% Cost Reduction</h3>
              <p className="text-gray-700 font-inter">
                Save $200K-400K annually compared to hiring a full-time General Counsel while getting senior-level expertise.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-8">
                <i className="ri-time-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Immediate Access</h3>
              <p className="text-gray-700 font-inter">
                Deploy experienced legal counsel within days, not months. Skip the lengthy hiring process.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-8">
                <i className="ri-scales-3-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Scale With Confidence</h3>
              <p className="text-gray-700 font-inter">
                Right-sized legal support that grows with your business from 10 hours to full-time coverage.
              </p>
            </div>
          </div>

          <div className="mt-16 text-center">
            <a href="/about" className="bg-black text-white px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer inline-block font-inter">
              View Detailed Comparison
            </a>
          </div>
        </div>
      </section>

      {/* How It Works - Simplified */}
      <section id="how-it-works" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-gray-900 mb-6 font-playfair">Simple, Transparent Process</h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto font-inter">
              Get the legal support you need quickly and efficiently
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-black rounded-full flex items-center justify-center mx-auto mb-8">
                <span className="text-white text-2xl font-bold font-inter">1</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Initial Consultation</h3>
              <p className="text-gray-700 font-inter">
                60-minute assessment of your business model, legal needs, and growth objectives.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-black rounded-full flex items-center justify-center mx-auto mb-8">
                <span className="text-white text-2xl font-bold font-inter">2</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Tailored Engagement</h3>
              <p className="text-gray-700 font-inter">
                Customized service package with predictable monthly pricing from 10-40 hours per month.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-black rounded-full flex items-center justify-center mx-auto mb-8">
                <span className="text-white text-2xl font-bold font-inter">3</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Integrated Partnership</h3>
              <p className="text-gray-700 font-inter">
                Seamless integration with your team through multiple channels and proactive strategy development.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl font-bold text-white mb-8 font-playfair">
            Ready to Build Your Legal Foundation?
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto font-inter">
            Schedule a consultation to discuss how fractional general counsel services can support your growth.
          </p>
          <a href="#contact" className="bg-teal-600 text-white px-12 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer inline-block font-inter">
            Get Started
          </a>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6 font-playfair">Get Started Today</h2>
            <p className="text-xl text-gray-700 font-inter">
              Choose the option that works best for you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="text-center">
              <div className="bg-gray-50 p-10 rounded-2xl border border-gray-200">
                <div className="w-16 h-16 bg-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="ri-calendar-line text-white text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Schedule a Call</h3>
                <p className="text-gray-700 mb-6 font-inter">
                  Book a consultation directly into my calendar.
                </p>
                <a 
                  href="https://calendly.com/sergio-vtclegal" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-teal-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer inline-block font-inter"
                >
                  Book via Calendly
                </a>
              </div>
            </div>

            <div className="text-center">
              <div className="bg-gray-50 p-10 rounded-2xl border border-gray-200">
                <div className="w-16 h-16 bg-gray-900 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="ri-mail-line text-white text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Send an Email</h3>
                <p className="text-gray-700 mb-6 font-inter">
                  Prefer to reach out directly? Send me an email.
                </p>
                <a 
                  href="mailto:sergio@vtclegal.com?subject=Legal Consultation Inquiry"
                  className="bg-gray-900 text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer inline-block font-inter"
                >
                  Email Me Directly
                </a>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 max-w-2xl mx-auto">
              <h3 className="text-xl font-bold text-gray-900 mb-3 font-playfair">Contact Information</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-center">
                  <i className="ri-mail-line text-teal-600 mr-2"></i>
                  <span className="font-inter text-gray-900">sergio@vtclegal.com</span>
                </div>
                <div className="flex items-center justify-center">
                  <i className="ri-phone-line text-teal-600 mr-2"></i>
                  <a href="tel:+14433067487" className="font-inter text-gray-900 hover:text-teal-600 transition-colors cursor-pointer">(443) 306-7487</a>
                </div>
                <div className="flex items-center justify-center">
                  <i className="ri-map-pin-line text-teal-600 mr-2"></i>
                  <span className="font-inter text-gray-900">Licensed in New York & California</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Simplified */}
      <footer className="bg-gray-50 text-gray-900 py-16 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <img 
                src="https://static.readdy.ai/image/a19705f045df358eda503236ce656b36/846ee1e93f81f94b53dc3e9fa39ac791.png" 
                alt="VTC Legal" 
                className="h-8 w-auto mb-6"
              />
              <p className="text-gray-700 mb-6 max-w-md leading-relaxed font-inter">
                Fractional general counsel services for SaaS and AI companies. Strategic legal guidance when you need it most.
              </p>
              <div className="flex space-x-4">
                <a href="https://www.linkedin.com/in/sergio-torres-law/" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
                  <i className="ri-linkedin-line text-xl"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-6 font-playfair text-lg">Services</h4>
              <ul className="space-y-3 text-gray-700">
                <li><a href="/ai-governance" className="hover:text-teal-600 transition-colors cursor-pointer font-inter">AI Governance</a></li>
                <li><a href="/commercial-strategy" className="hover:text-teal-600 transition-colors cursor-pointer font-inter">Commercial Strategy</a></li>
                <li><a href="/corporate-law" className="hover:text-teal-600 transition-colors cursor-pointer font-inter">Corporate Law</a></li>
                <li><a href="/strategic-planning" className="hover:text-teal-600 transition-colors cursor-pointer font-inter">Strategic Planning</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-6 font-playfair text-lg">Contact</h4>
              <ul className="space-y-3 text-gray-700">
                <li className="font-inter">sergio@vtclegal.com</li>
                <li className="font-inter">(443) 306-7487</li>
                <li className="font-inter">Licensed in NY & CA</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-300 pt-8 mt-12 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-600 text-sm font-inter">
              © 2026 VTC Legal. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 mt-4 md:mt-0">
              <a href="/privacy-policy" className="text-gray-600 hover:text-teal-600 text-sm transition-colors cursor-pointer font-inter">Privacy Policy</a>
              <a href="/terms-of-service" className="text-gray-600 hover:text-teal-600 text-sm transition-colors cursor-pointer font-inter">Terms of Service</a>
              <a href="https://readdy.ai/?origin=logo" className="text-gray-600 hover:text-teal-600 text-sm transition-colors cursor-pointer font-inter">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
